/**
 * The filter model a grid's stat cards drive.
 *
 * EntityGrid owns one of these for every grid that shows descriptor cards, so
 * a card click filters the grid with no per-page wiring. A page that renders
 * its own filter chips over the same params owns the model instead and passes
 * it in, which is what keeps a card and a chip over one param from disagreeing
 * — and what lets the page fold the same values into the bulk-action filters
 * its select-all bar sends.
 *
 * Contains:
 * - useGridCardFilters — persisted card-filter model + its stable operations
 */

import { useCallback, useMemo } from 'react';

import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import {
  isStatCardFilterActive,
  setStatCardFilter,
  statCardFilterValues,
  toFilterParams,
  toggleStatCardFilter,
} from '@sophocles/data-grids/grid/statCardFilters';
import usePersistedState from '@sophocles/hooks/usePersistedState';

const EMPTY_MODEL = {};

/**
 * @param {string|null} [persistKey] - fills `grid:<persistKey>:ct<customerTypeId>:cardFilters`.
 *   Null or omitted keeps the model in memory for the life of the mount.
 * @returns {{
 *   model: Object<string, *>,
 *   params: Object<string, string>,
 *   toggle: (filters?: Object<string, string>, ownedParams?: Iterable<string>) => void,
 *   setFilter: (param: string, value: *) => void,
 *   isActive: (filters?: Object<string, (string|string[])>, ownedParams?: Iterable<string>) => boolean,
 *   values: (param: string) => string[],
 *   clear: () => void,
 * }}
 */
export default function useGridCardFilters(persistKey) {
  const [model, setModel] = usePersistedState(useGridSessionKey(persistKey, 'cardFilters'), EMPTY_MODEL);

  const toggle = useCallback(
    // ownedParams comes from the card row, which the grid knows and this hook
    // does not: a click clears the params those cards own and nothing else.
    (filters, ownedParams) => setModel(current => toggleStatCardFilter(current, filters, ownedParams)),
    [setModel],
  );

  const setFilter = useCallback(
    (param, value) => setModel(current => setStatCardFilter(current, param, value)),
    [setModel],
  );

  const clear = useCallback(() => setModel(EMPTY_MODEL), [setModel]);

  return useMemo(
    () => ({
      model,
      params: toFilterParams(model),
      toggle,
      setFilter,
      clear,
      isActive: (filters, ownedParams) => isStatCardFilterActive(model, filters, ownedParams),
      values: param => statCardFilterValues(model, param),
    }),
    [model, toggle, setFilter, clear],
  );
}
